<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BulkContractServiceFeeEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    items: {
      type: Array,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getItemFromInput = (...args) => {
    const [inputItems, fields] = args;
    const fieldNames = _.map(fields, 'name');

    let result = {};

    _.each(inputItems, (item, i) => {
      if (i === 0) {
        result = _.pick(item, fieldNames);
      }

      for (const { name, defaultValue } of fields) {
        if (result[name] != item[name]) {
          result[name] = defaultValue;
          continue;
        }

        if (_.isUndefined(result[name])) {
          result[name] = defaultValue;
          continue;
        }

        if (result[name] != null) {
          result[name] = item[name];
        }
      }
    });

    result['$updates'] = _.reduce(fieldNames, (m, v) => _.set(m, v, false), {});
    return result;
  };
  const getPayload = (...args) => {
    const [inputItems, item] = args;

    const updates = _.keys(_.pickBy(item.$updates, _.identity));

    if (!updates.length) {
      return;
    }

    const update = _.pick(item, updates);

    return {
      items: _.map(inputItems, (item) => ({
        id: item.id,
        etag: item.etag,
        value: update,
      })),
    };
  };

  const editableFields = ref([
    {
      name: 'valid_from',
    },
    {
      name: 'valid_to',
    },
    {
      name: 'amount',
    },
    {
      name: 'currency',
    },
    {
      name: 'unit',
    },
    {
      name: 'quantity',
    },
    {
      name: 'unit_of_quantity',
    },
    {
      name: 'inflation_indexed',
    },
    {
      name: 'transaction_notes',
    },
    {
      name: 'notes',
    },
  ]);
  const labelAlign = ref('right');
  const formId = ref(null);
  const formName = ref('BulkContractServiceFeeEditor');
  const item = ref(null);
  const currencies = ref(null);
  const paymentMethods = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    editableFields: editableFields.value,
    labelAlign: labelAlign.value,
    formId: formId.value,
    formName: formName.value,
    item: item.value,
    currencies: currencies.value,
    paymentMethods: paymentMethods.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return getItemFromInput(props.items, $data.value.editableFields);
  };
  const $$load_currencies = () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_paymentMethods = () => {
    return $fn.value
      .fetch('list_payment_method')
      .then((data) =>
        _.reduce(data, (m, v, k) =>
          _.set(m, v.id, $fn.value.pgettext('PaymentMethod', v.name))
        )
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'editableFields' },
      { name: 'labelAlign' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'item' },
      { name: 'currencies' },
      { name: 'paymentMethods' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      item: $$load_item,
      currencies: $$load_currencies,
      paymentMethods: $$load_paymentMethods,
    },
    ux: {},
    dataRefs: {
      editableFields,
      labelAlign,
      formId,
      formName,
      item,
      currencies,
      paymentMethods,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'BulkContractServiceFeeEditor|Title',
        'Bulk Edit'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.items,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('items');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_4nv2sqldx_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_a3hmw3nih_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_request_kqorink08_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'bulk_update_contract_service_fee',
      data: getPayload(props.items, item.value),
    };
  };

  const act_7315092382398562_closeModal_4nv2sqldx =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_4nv2sqldx_getActionArgs,
    });
  const inst_evh_7315092382398562_close_xrxrnz3k7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_4nv2sqldx,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_xrxrnz3k7],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_kl2a4orxo =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_s81q07uzg =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_kl2a4orxo,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_s81q07uzg],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_bem6uwja3 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_zl3t5r9fz =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_bem6uwja3,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_zl3t5r9fz],
    event: 'close',
  });
  const act_1040167445267876_closeModal_a3hmw3nih =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_a3hmw3nih_getActionArgs,
    });
  const inst_evh_1040167445267876_success_0jmgaapyk =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_a3hmw3nih,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_kqorink08 = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_kqorink08_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_0jmgaapyk],
  });
  const inst_evh_1040167445267876_submit_km9bmobyp =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_kqorink08,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_km9bmobyp],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_5rlybjij9 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_6mcuaobfn =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_5rlybjij9,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_6mcuaobfn],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_xrxrnz3k7,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_s81q07uzg,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_zl3t5r9fz,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_0jmgaapyk,
    inst_evh_1040167445267876_submit_km9bmobyp,
    save: inst_evh_1040167445267876_submit_km9bmobyp,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_6mcuaobfn,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_xrxrnz3k7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_xrxrnz3k7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_s81q07uzg = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_s81q07uzg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_zl3t5r9fz = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_zl3t5r9fz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_0jmgaapyk = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_0jmgaapyk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_km9bmobyp = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_km9bmobyp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_6mcuaobfn = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_6mcuaobfn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BulkContractServiceFeeEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :resourceTitle="
          pgettext(
            'BulkContractServiceFeeEditor|ResourceTitle',
            'Contract Service Fees'
          )
        "
        :resourceKey="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        autofocus="first"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup
          name="valid_from"
          :label="gettext('Valid From')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="valid_from_enabled"
                v-model="item.$updates.valid_from"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormDatetime
            name="valid_from"
            v-model="item.valid_from"
            :disabled="!item.$updates.valid_from"
            class="w-auto"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_to"
          :label="gettext('Valid To')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="valid_to_enabled"
                v-model="item.$updates.valid_to"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormDatetime
            name="valid_to"
            v-model="item.valid_to"
            :disabled="!item.$updates.valid_to"
            class="w-auto"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="amount"
          :label="pgettext('ContractServiceFee', 'Amount')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="amount_enabled"
                v-model="item.$updates.amount"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormInput
            name="amount"
            v-model="item.amount"
            :disabled="!item.$updates.amount"
            mask="number"
            unmask="typed"
            :scale="4"
            class="w-auto"
          >
          </ItpFormInput>
          <ItpFormNativeSelect
            name="currency"
            :options="currencies"
            v-model="item.currency"
            :disabled="!item.$updates.amount"
            textField="code"
            valueField="code"
            class="w-auto"
          >
          </ItpFormNativeSelect>
        </ItpFormGroup>
        <ItpFormGroup
          name="quantity"
          :label="gettext('Quantity')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="quantity_enabled"
                v-model="item.$updates.quantity"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormInput
            type="number"
            name="quantity"
            v-model="item.quantity"
            :disabled="!item.$updates.quantity"
            class="w-auto"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="unit_of_quantity"
          :label="pgettext('ContractServiceFee', 'Unit of quantity')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="unit_of_quantity_enabled"
                v-model="item.$updates.unit_of_quantity"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormInput
            type="number"
            name="unit_of_quantity"
            v-model="item.unit_of_quantity"
            :disabled="!item.$updates.unit_of_quantity"
            class="w-auto"
          >
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          name="inflation_indexed"
          :label="pgettext('ContractServiceFee', 'Inflation indexed')"
          :label-cols-md="4"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="inflation_indexed_enabled"
                v-model="item.$updates.inflation_indexed"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormCheckbox
            type="number"
            name="inflation_indexed"
            v-model="item.inflation_indexed"
            :disabled="!item.$updates.inflation_indexed"
            class="w-auto"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          name="transaction_notes"
          :label="pgettext('ContractServiceFee', 'Transaction notes')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="transaction_notes_enabled"
                v-model="item.$updates.transaction_notes"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <TransactionNotesEditor
            v-model="item.transaction_notes"
            :disabled="!item.$updates.transaction_notes"
          >
          </TransactionNotesEditor>
        </ItpFormGroup>
        <hr class="my-4" />
        <ItpFormGroup
          name="notes"
          :label="pgettext('Invoice', 'Notes')"
          :label-cols-md="4"
          :content-cols-md="8"
          :labelAlign="labelAlign"
          contentClass="input-group"
          class="col"
        >
          <div class="input-group-prepend">
            <div class="input-group-text">
              <ItpFormCheckbox
                name="notes_enabled"
                v-model="item.$updates.notes"
              >
              </ItpFormCheckbox>
            </div>
          </div>
          <ItpFormTextarea
            name="notes"
            v-model="item.notes"
            :disabled="!item.$updates.notes"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms[formName] || !forms[formName].submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
